'use client';
import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';

type BootPhase = 'bios' | 'loading' | 'splash' | 'done';

const BIOS_LINES = [
  { text: 'RetroDesk BIOS v2.1.4  Copyright (C) 1998-2026', highlight: true, delay: 0 },
  { text: 'CPU: Intel Pentium II 450MHz', delay: 120 },
  { text: 'Memory Test: 524288K OK', delay: 240 },
  { text: 'Detecting Primary Master... ST340015A (38147 MB)', delay: 380 },
  { text: 'Detecting Primary Slave... None', delay: 500 },
  { text: 'Detecting Secondary Master... ATAPI CD-ROM Drive', delay: 620 },
  { text: 'Detecting Secondary Slave... None', delay: 740 },
  { text: '', delay: 820 },
  { text: 'PnP Init Completed', delay: 900 },
  { text: 'AGP v2.0 64MB Detected', delay: 980 },
  { text: 'USB Controllers: 2 Found', delay: 1060 },
  { text: '', delay: 1120 },
  { text: 'Award Modular BIOS v4.51PG, An Energy Star Ally', delay: 1200 },
  { text: 'Copyright (C) 1984-2026, Award Software, Inc.', delay: 1280 },
  { text: '', delay: 1340 },
  { text: 'Press DEL to enter SETUP, F8 for Boot Menu', delay: 1400, highlight: true },
  { text: '', delay: 1500 },
  { text: 'Verifying DMI Pool Data......', delay: 1600 },
  { text: 'Boot from CD-ROM: Failure', delay: 1900 },
  { text: 'Boot from HDD-0: Success', delay: 2100, highlight: true },
  { text: '', delay: 2200 },
  { text: 'Loading RetroDesk OS...', delay: 2400, highlight: true },
];

export default function BootScreen() {
  const router = useRouter();
  const [phase, setPhase] = useState<BootPhase>('bios');
  const [visibleLines, setVisibleLines] = useState<number>(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const progressRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Show BIOS lines one by one
    const timers: NodeJS.Timeout[] = [];

    BIOS_LINES.forEach((line, idx) => {
      const t = setTimeout(() => {
        setVisibleLines(idx + 1);
      }, line.delay + 300);
      timers.push(t);
    });

    // After all lines, go to loading phase
    const phaseTimer = setTimeout(() => {
      setPhase('loading');
    }, 3200);
    timers.push(phaseTimer);

    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (phase !== 'loading') return;

    let progress = 0;
    progressRef.current = setInterval(() => {
      progress += Math.random() * 4 + 1;
      if (progress >= 100) {
        progress = 100;
        clearInterval(progressRef.current!);
        setTimeout(() => setPhase('splash'), 400);
      }
      setLoadingProgress(Math.min(progress, 100));
    }, 80);

    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'splash') return;
    const t = setTimeout(() => {
      setFadeOut(true);
      setTimeout(() => {
        localStorage.setItem('retrodesk-booted', '1');
        router.push('/desktop-interface');
      }, 800);
    }, 1800);
    return () => clearTimeout(t);
  }, [phase, router]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black flex flex-col overflow-hidden"
        initial={{ opacity: 1 }}
        animate={{ opacity: fadeOut ? 0 : 1 }}
        transition={{ duration: 0.8 }}
        style={{ fontFamily: 'var(--font-pixel)' }}
      >
        {/* BIOS Phase */}
        {phase === 'bios' && (
          <div className="p-4 md:p-8 flex-1 overflow-hidden">
            <div className="bios-text space-y-0">
              {BIOS_LINES.slice(0, visibleLines).map((line, idx) => (
                <div
                  key={`bios-line-${idx}`}
                  className={`leading-6 ${line.highlight ? 'bios-highlight' : ''}`}
                >
                  {line.text || '\u00A0'}
                </div>
              ))}
              {visibleLines < BIOS_LINES.length && (
                <span className="terminal-cursor" />
              )}
            </div>
          </div>
        )}

        {/* Loading Phase */}
        {phase === 'loading' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8">
            {/* RetroDesk Logo text */}
            <div className="text-center">
              <div
                className="font-pixel text-white mb-2"
                style={{ fontSize: '48px', lineHeight: 1 }}
              >
                RetroDesk
              </div>
              <div
                className="font-pixel text-gray-400"
                style={{ fontSize: '20px' }}
              >
                Professional Edition
              </div>
            </div>

            {/* Windows-style loading bar */}
            <div className="w-64">
              <div className="progress-bar-win">
                <div
                  className="progress-bar-win-fill"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
              <div
                className="font-pixel text-gray-400 text-center mt-2"
                style={{ fontSize: '14px' }}
              >
                Loading... {Math.floor(loadingProgress)}%
              </div>
            </div>

            <div
              className="font-pixel text-gray-600"
              style={{ fontSize: '14px' }}
            >
              Please wait while RetroDesk initializes
            </div>
          </div>
        )}

        {/* Splash Phase */}
        {phase === 'splash' && (
          <motion.div
            className="flex-1 flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {/* Big Windows-style logo */}
            <div className="text-center">
              {/* Pixel art windows logo */}
              <div className="flex justify-center mb-6">
                <div className="grid grid-cols-2 gap-1" style={{ width: 64, height: 64 }}>
                  <div className="bg-red-500" style={{ borderRadius: 0 }} />
                  <div className="bg-green-500" style={{ borderRadius: 0 }} />
                  <div className="bg-blue-500" style={{ borderRadius: 0 }} />
                  <div className="bg-yellow-400" style={{ borderRadius: 0 }} />
                </div>
              </div>
              <div
                className="font-pixel text-white"
                style={{ fontSize: '56px', lineHeight: 1 }}
              >
                RetroDesk
              </div>
              <div
                className="font-pixel text-blue-400 mt-2"
                style={{ fontSize: '22px' }}
              >
                Starting Windows...
              </div>
            </div>

            {/* Animated dots */}
            <div className="flex gap-2 mt-8">
              {[0, 1, 2, 3].map((i) => (
                <motion.div
                  key={`dot-${i}`}
                  className="w-3 h-3 bg-blue-400"
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{
                    duration: 1.2,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Scanlines overlay */}
        <div className="scanlines" />
      </motion.div>
    </AnimatePresence>
  );
}